import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { leaveRulesApi, GENDER_OPTIONS, ACCUMULATE_OPTIONS } from '../api/leaveRulesApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';

const DEFAULT_DETAIL_FORM = {
  leaveTypeId: '',
  totalDays: 10,
  minDays: 1,
  maxDays: 5,
  cfDays: 0,
  accumulate: 'No',
  lapseDays: 0,
  extraDays: 0,
  gender: 'A',
  sandwichPresent: false,
};

export function useLeaveRulesDetail() {
  const { id: routeRuleId } = useParams();
  const navigate = useNavigate();
  const ruleId = routeRuleId;

  const [rule, setRule] = useState(null);
  const [details, setDetails] = useState([]);
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal State for Add / Edit Rule Detail
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDetailId, setEditingDetailId] = useState(null);
  const [detailForm, setDetailForm] = useState(DEFAULT_DETAIL_FORM);
  const [isSavingDetail, setIsSavingDetail] = useState(false);

  const loadData = useCallback(async () => {
    if (!ruleId) {
      navigate('/payroll/leave-rules');
      return;
    }
    setIsLoading(true);
    try {
      const [ruleData, detailsData, typeOptions] = await Promise.all([
        leaveRulesApi.getRuleById(ruleId),
        leaveRulesApi.getRuleDetails(ruleId),
        leaveRulesApi.getLeaveTypeOptions(),
      ]);

      if (ruleData) {
        setRule(ruleData);
      } else {
        appToast.error('Leave rule not found.');
        navigate('/payroll/leave-rules');
        return;
      }

      setDetails(detailsData || []);
      setLeaveTypeOptions(typeOptions || []);
    } catch (err) {
      appToast.error('Failed to load leave rule details.');
    } finally {
      setIsLoading(false);
    }
  }, [ruleId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openAddModal = () => {
    setEditingDetailId(null);
    setDetailForm({
      ...DEFAULT_DETAIL_FORM,
      leaveTypeId: leaveTypeOptions[0]?.value ? String(leaveTypeOptions[0].value) : '',
    });
    setIsModalOpen(true);
  };

  const openEditModal = (detail) => {
    setEditingDetailId(detail.id);
    setDetailForm({
      leaveTypeId: String(detail.leaveTypeId || ''),
      totalDays: detail.totalDays ?? 10,
      minDays: detail.minDays ?? 1,
      maxDays: detail.maxDays ?? 5,
      cfDays: detail.cfDays ?? 0,
      accumulate: detail.accumulate || 'No',
      lapseDays: detail.lapseDays ?? 0,
      extraDays: detail.extraDays ?? 0,
      gender: detail.gender || 'A',
      sandwichPresent: Boolean(detail.sandwichPresent),
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingDetailId(null);
    setDetailForm(DEFAULT_DETAIL_FORM);
  };

  const patchDetailForm = (field, value) => {
    setDetailForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDetail = async () => {
    if (!detailForm.leaveTypeId) {
      appToast.error('Please select a leave type.');
      return;
    }

    const total = Number(detailForm.totalDays || 0);
    const extra = Number(detailForm.extraDays || 0);
    if (total + extra <= 0) {
      appToast.error('Enter total days or extra days.');
      return;
    }

    setIsSavingDetail(true);
    try {
      if (editingDetailId) {
        await leaveRulesApi.updateRuleDetail(editingDetailId, {
          ...detailForm,
          ruleId: Number(ruleId),
          ruleName: rule?.name || 'Rule',
        });
        appToast.success('Leave quota details updated!');
      } else {
        await leaveRulesApi.createRuleDetail({
          ...detailForm,
          ruleId: Number(ruleId),
          ruleName: rule?.name || 'Rule',
        });
        appToast.success('Leave quota details added!');
      }
      closeModal();
      loadData();
    } catch (err) {
      const errMsg = err?.response?.data?.detail || 'Failed to save leave quota details.';
      appToast.error(errMsg);
    } finally {
      setIsSavingDetail(false);
    }
  };

  const handleDeleteDetail = async (id, leaveTypeName) => {
    const confirmed = await appConfirm({
      title: 'Remove Leave Quota',
      message: `Are you sure you want to remove ${leaveTypeName || 'this'} quota from "${rule?.name || 'this rule'}"?`,
      confirmText: 'Remove Quota',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    try {
      await leaveRulesApi.deleteRuleDetail(id);
      appToast.success(`${leaveTypeName || 'Quota'} removed successfully.`);
      loadData();
    } catch (err) {
      appToast.error('Failed to delete quota detail.');
    }
  };

  return {
    state: {
      rule,
      ruleId,
      details,
      isLoading,
      isModalOpen,
      editingDetailId,
      detailForm,
      isSavingDetail,
      leaveTypeOptions,
      genderOptions: GENDER_OPTIONS,
      accumulateOptions: ACCUMULATE_OPTIONS,
    },
    actions: {
      openAddModal,
      openEditModal,
      closeModal,
      patchDetailForm,
      handleSaveDetail,
      handleDeleteDetail,
      refresh: loadData,
    },
  };
}
